import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { publicFormApi } from '../services/public-form.service';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function PublicFormPage() {
  const { token } = useParams<{ token: string }>();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { data: form, isLoading, isError } = useQuery({
    queryKey: ['public-form', token],
    queryFn: () => publicFormApi.getByToken(token!),
    enabled: !!token,
    staleTime: 0,
  });

  const currentStep = form?.pages?.[stepIndex];

  const themeStyles = useMemo(() => {
    if (!form?.theme) return {};
    return {
      backgroundColor: form.theme.backgroundColor || '#f8fafc',
      color: form.theme.textColor || '#0f172a',
      backgroundImage: form.theme.backgroundImageUrl ? `url(${form.theme.backgroundImageUrl})` : undefined,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    } as CSSProperties;
  }, [form?.theme]);

  if (isLoading) {
    return <div className="min-h-screen grid place-items-center">Carregando formulário...</div>;
  }

  if (isError || !form) {
    return <div className="min-h-screen grid place-items-center">Formulário não encontrado.</div>;
  }

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await publicFormApi.submit(form.token, answers, true);
      setSubmitted(true);
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || 'Falha ao enviar respostas.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-10">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h1 className="text-2xl font-bold">Resposta enviada</h1>
            <p className="text-muted-foreground">Obrigado por responder este formulário.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <Card className="max-w-4xl mx-auto">
        <CardHeader className="space-y-2 border-b" style={themeStyles}>
          <CardTitle className="text-2xl">{form.name}</CardTitle>
          {form.description && <p className="text-sm opacity-90">{form.description}</p>}
          <Badge variant="outline" className="w-fit">
            Página {stepIndex + 1} de {form.pages.length}
          </Badge>
        </CardHeader>

        <CardContent className="p-6 space-y-4">
          {currentStep && (
            <>
              <div>
                <h2 className="text-xl font-semibold">{currentStep.title}</h2>
                <p className="text-sm text-muted-foreground mt-1">{currentStep.content}</p>
              </div>

              <div className="space-y-3">
                {(currentStep.questions || []).map((question) => {
                  const value = answers[question.id];
                  return (
                    <Card key={question.id}>
                      <CardContent className="p-4 space-y-2">
                        <label className="text-sm font-medium block">
                          {question.label}
                          {question.required && <span className="text-destructive"> *</span>}
                        </label>

                        {question.type === 'text' && (
                          <Input
                            value={value || ''}
                            onChange={(e) => setAnswers((prev) => ({ ...prev, [question.id]: e.target.value }))}
                            placeholder="Digite sua resposta"
                          />
                        )}

                        {question.type === 'single' && (
                          <div className="space-y-1">
                            {(question.options || []).map((opt, idx) => (
                              <label key={`${question.id}_${idx}`} className="flex items-center gap-2 text-sm">
                                <input
                                  type="radio"
                                  name={question.id}
                                  checked={value === opt}
                                  onChange={() => setAnswers((prev) => ({ ...prev, [question.id]: opt }))}
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        )}

                        {question.type === 'multiple' && (
                          <div className="space-y-1">
                            {(question.options || []).map((opt, idx) => {
                              const current = Array.isArray(value) ? value : [];
                              const checked = current.includes(opt);
                              return (
                                <label key={`${question.id}_${idx}`} className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      setAnswers((prev) => {
                                        const existing = Array.isArray(prev[question.id]) ? [...prev[question.id]] : [];
                                        if (e.target.checked) {
                                          if (!existing.includes(opt)) existing.push(opt);
                                        } else {
                                          const removeIndex = existing.indexOf(opt);
                                          if (removeIndex >= 0) existing.splice(removeIndex, 1);
                                        }
                                        return { ...prev, [question.id]: existing };
                                      });
                                    }}
                                  />
                                  {opt}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              onClick={() => setStepIndex((prev) => Math.max(0, prev - 1))}
              disabled={stepIndex === 0}
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Anterior
            </Button>

            {stepIndex < form.pages.length - 1 ? (
              <Button onClick={() => setStepIndex((prev) => Math.min(form.pages.length - 1, prev + 1))}>
                Próxima <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Enviando...' : 'Enviar formulário'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
